import type { AttributeHistoryPointResponse } from '../../api/types';
import { formatTimestamp } from './attributeDisplay';
import styles from './AttributeHistoryPlot.module.css';

/**
 * The recorded points of one attribute's history, drawn.
 *
 * ---------------------------------------------------------------------
 * WHY THIS IS HAND-DRAWN SVG AND NOT A CHART LIBRARY
 *
 * docs/TECHNICAL_ARCHITECTURE.md §5 left the chart library open, and this
 * is the first view in the product that plots anything, so the decision
 * lands here. It is: **no library** — see docs/FRONTEND.md §5b for the
 * alternatives weighed. The short version is that a general-purpose
 * charting library's defaults are precisely the things this drawing must
 * not do (smooth curves, continuous axes, a value readable only by
 * hovering), and turning those defaults off costs more than the eighty
 * lines below.
 *
 * WHAT THE DRAWING PROMISES
 *
 * 1. **Only recorded points exist.** Each one is an explicit marker with
 *    its value written beside it. Nothing is interpolated, smoothed,
 *    averaged or extrapolated, and no marker is drawn anywhere a record
 *    does not exist.
 * 2. **The connectors are dashed on purpose.** They carry sequence, not
 *    measurement — docs/ASSESSMENT_FRAMEWORK.md §7's own illustration is
 *    `12 → 14 → 15 → 17` with dates beneath, which is a sequence of
 *    labelled points rather than a curve. A solid line between two annual
 *    reviews would assert twelve months of measurement that never
 *    happened.
 * 3. **The x axis is real elapsed time.** Points sit where their dates put
 *    them, so a nine-month gap and a three-year gap look different. Evenly
 *    spacing them would imply a regular assessment cadence.
 * 4. **The y axis is the whole 1-20 scale, always.** Fitting the axis to
 *    the data is the standard way a chart exaggerates a change: on a
 *    zoomed axis, 14 → 15.3 climbs dramatically. On the real scale it
 *    moves as far as it actually moved.
 * 5. **Nothing here needs a pointer.** Every value and date is drawn as
 *    text; there are no tooltips, and the table beneath carries the full
 *    detail regardless. The whole figure is `role="img"` with a label that
 *    reads out every point, because a screen reader should not have to
 *    reconstruct a shape from coordinates.
 * 6. **One hue.** The markers do not carry band colour: position already
 *    encodes the value, and colouring by band would add a second, redundant
 *    channel that some readers cannot see and that reads as a verdict.
 * ---------------------------------------------------------------------
 */

const WIDTH = 640;
const HEIGHT = 200;
const PAD = { top: 18, right: 26, bottom: 34, left: 30 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

/** The presentation scale (docs/ATTRIBUTE_FRAMEWORK.md). Never rescaled to
 * 0-100 and never fitted to the data — see rule 4 above. */
const SCALE_MIN = 1;
const SCALE_MAX = 20;
const GRID_VALUES = [1, 5, 10, 15, 20];

/** Beyond this many points the per-point text labels would collide, so they
 * are dropped and the table alone carries the values. The markers stay. */
const DENSE_ABOVE = 6;

export function AttributeHistoryPlot({
  points,
  attributeName,
}: {
  points: AttributeHistoryPointResponse[];
  attributeName: string;
}) {
  const times = points.map((p) => new Date(p.asOf).getTime());
  const first = Math.min(...times);
  const last = Math.max(...times);
  const span = last - first;

  const x = (time: number) => (span === 0 ? PAD.left + PLOT_W / 2 : PAD.left + ((time - first) / span) * PLOT_W);
  const y = (value: number) =>
    PAD.top + PLOT_H - ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * PLOT_H;

  const placed = points.map((point, index) => ({
    point,
    index,
    cx: x(times[index]!),
    cy: y(point.composite.composite),
  }));

  const dense = points.length > DENSE_ABOVE;
  const latestIndex = points.length - 1;

  /* The spoken equivalent of the whole figure: every recorded point, in
   * order, as value-and-date. Deliberately descriptive rather than
   * interpretive — it does not say "rising", because which direction this
   * amounts to is the domain's conclusion, stated separately and taken
   * from the API. */
  const label =
    `${attributeName}: ${points.length} recorded ${points.length === 1 ? 'point' : 'points'}. ` +
    placed
      .map(({ point }) => `${point.composite.composite.toFixed(1)} out of 20 on ${formatTimestamp(point.asOf)}`)
      .join('; ') +
    '. Nothing was recorded between these dates.';

  return (
    <figure className={styles.figure}>
      <svg
        className={styles.plot}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={label}
      >
        {GRID_VALUES.map((value) => (
          <g key={value}>
            <line
              className={styles.grid}
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(value)}
              y2={y(value)}
            />
            <text className={styles.axisLabel} x={PAD.left - 8} y={y(value) + 3} textAnchor="end">
              {value}
            </text>
          </g>
        ))}

        {/* Sequence connectors. Straight segments between consecutive
            recorded points, dashed, never curved — see rule 2. */}
        {placed.slice(1).map(({ cx, cy, index }) => {
          const previous = placed[index - 1]!;
          return (
            <line
              key={`link-${index}`}
              className={styles.connector}
              x1={previous.cx}
              y1={previous.cy}
              x2={cx}
              y2={cy}
            />
          );
        })}

        {placed.map(({ point, index, cx, cy }) => {
          const isLatest = index === latestIndex;
          return (
            <g key={point.asOf}>
              {isLatest ? <circle className={styles.currentRing} cx={cx} cy={cy} r={8} /> : null}
              <circle className={styles.point} cx={cx} cy={cy} r={4.5} />
              {dense ? null : (
                <text className={styles.valueLabel} x={cx} y={cy - 12} textAnchor="middle">
                  {point.composite.composite.toFixed(1)}
                </text>
              )}
              {dense && index !== 0 && !isLatest ? null : (
                <text className={styles.dateLabel} x={cx} y={HEIGHT - PAD.bottom + 20} textAnchor="middle">
                  {formatTimestamp(point.asOf)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <figcaption className={styles.caption}>
        Each marker is one recorded point on the 1–20 scale, positioned by the date it was recorded. The dashed
        connectors show the order of the points and nothing more — no assessment exists between them, and none has
        been estimated. The ringed marker is the most recent point. The full axis is shown so a change is not
        magnified by fitting the scale to the data.
      </figcaption>
    </figure>
  );
}
